import { IconDatabase } from '@rossum/ui/icons/tabler';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useIntl } from '../ComponentsContext';
import { boldText } from './boldText';
import { HighlightMatches } from './searchMatching';
import { menuListSharedProps } from './shared';
import { SuggesterPopoverMode, VariableCompletion } from './types';

export const MenuItemContentDatasetColumn = ({
  completion,
  search,
}: {
  completion: VariableCompletion;
  search: string;
}) => (
  <>
    <ListItemIcon>
      <SvgIcon fontSize="medium" color="secondary">
        <IconDatabase />
      </SvgIcon>
    </ListItemIcon>
    <ListItemText
      primary={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <HighlightMatches text={completion.caption} search={search} />
          </Typography>
        </Box>
      }
      secondary={
        <>
          {completion.value && (
            <Typography
              variant="caption"
              color="info.main"
              component="div"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <HighlightMatches text={completion.value} search={search} />
            </Typography>
          )}
          {completion.description ? (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontStyle: 'italic',
              }}
            >
              {completion.description}
            </Typography>
          ) : null}
        </>
      }
      primaryTypographyProps={{
        component: 'div',
      }}
      secondaryTypographyProps={{
        component: 'div',
      }}
      sx={{
        my: 0,
      }}
    />
  </>
);

export const MenuListDatasetColumns = ({
  completions,
  onInsert,
  search,
  mode,
  setFirstRef,
}: {
  completions: VariableCompletion[];
  onInsert: (value: string) => void;
  search: string;
  mode: SuggesterPopoverMode;
  setFirstRef: React.MutableRefObject<HTMLLIElement | null>;
}) => {
  const intl = useIntl();

  if (completions.length === 0) {
    return (
      <Stack alignItems="center" p={2}>
        <Typography variant="body1" textAlign="center" color="text.primary">
          {intl.formatMessage({
            id: 'features.formulas.suggester.menuListDatasetColumns.noResults.title',
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intl.formatMessage(
            {
              id: 'features.formulas.suggester.menuListDatasetColumns.noResults.description',
            },
            {
              bold: boldText,
              search,
            }
          )}
        </Typography>
      </Stack>
    );
  }

  return (
    <MenuList {...menuListSharedProps}>
      {completions.map((completion, index) => {
        const insertValue =
          mode === 'fstring'
            ? `{${completion.meta}.${completion.value}}`
            : `${completion.meta}.${completion.value}`;

        return (
          <MenuItem
            key={completion.value}
            ref={index === 0 ? setFirstRef : null}
            onClick={() => onInsert(insertValue)}
            sx={{
              alignItems: 'center',
            }}
          >
            <MenuItemContentDatasetColumn
              completion={completion}
              search={search}
            />
          </MenuItem>
        );
      })}
    </MenuList>
  );
};
